import {
  Waves,
  HeartPulse,
  ListChecks,
  MessageSquareText,
  MapPin,
  BarChart3,
  Users,
  BookHeart,
  LayoutDashboard,
  SlidersHorizontal,
  HeartHandshake,
  Smile,
} from "lucide-react";

const S = `${process.env.PUBLIC_URL}/screenshots`;

export const FEATURES_DATA = {
  "calm-toolkit": {
    title: "Sensory-Safe Calm Toolkit",
    tagline: "Tools for when you need them most",
    icon: Waves,
    color: "brand-teal",
    bgColor: "bg-brand-teal/10",
    iconColor: "text-brand-teal",
    description:
      "Four digital fidgets and twelve guided grounding circuits, designed with zero scores, zero timers, and zero pressure. Bubble Pop, Ripple Pond, Feather Drift, and Fidget Spinner — each crafted to soothe, not stimulate.",
    extendedDescription:
      "The Calm Toolkit is your go-to space when the world feels too much. Choose from sensory-safe fidget games that respond gently to your touch, follow guided grounding circuits like the 5-4-3-2-1 technique, or try breathing exercises such as 4-4-6 breathing. Every tool is designed with your nervous system in mind — optional haptics and sound are off by default, and reduced motion mode is always available.",
    screenshots: [
      { url: `${S}/calm-1.webp`, caption: "Fidget Games — Choose from calming activities" },
      { url: `${S}/calm-2.webp`, caption: "Bubble Pop — Tap floating bubbles to pop them" },
      { url: `${S}/calm-3.webp`, caption: "Sensory Circuits — Guided grounding routines" },
      { url: `${S}/calm-4.webp`, caption: "Deep Pressure Hug — A grounding step" },
      { url: `${S}/calm-5.webp`, caption: "Breathing Exercise — 4-4-6 guided breathing" },
      { url: `${S}/calm-6.webp`, caption: "5-4-3-2-1 Grounding — Name what you can see" },
    ],
    keyPoints: [
      "4 sensory-safe fidget experiences",
      "12 guided grounding circuits (2-4 minutes each)",
      "Breathing exercises (4-4-6 and more)",
      "Optional haptics and sound — off by default",
      "Reduced motion mode for extra comfort",
      "No scores, no timers, no pressure",
    ],
  },
  "check-ins": {
    title: "Simple Check-Ins",
    tagline: "Emotional awareness, without the pressure",
    icon: HeartPulse,
    color: "brand-rose",
    bgColor: "bg-brand-rose/15",
    iconColor: "text-brand-rose",
    description:
      "Quick mood and energy snapshots that help you notice patterns — without streaks, scores, or guilt. Check in when you want, skip when you don't.",
    extendedDescription:
      "Check-ins are designed to be gentle and quick. Slide to select how you're feeling on a 1-10 scale, optionally note what's around you (at home, at work, with people, alone), and add a personal note if you'd like. Your check-ins build a picture of your patterns over time — with monthly summaries and insights that help you understand your experience without judgment.",
    screenshots: [
      { url: `${S}/checkins-1.webp`, caption: "Check-In — How are you feeling?" },
      { url: `${S}/checkins-2.webp`, caption: "My Check-Ins — Monthly summary and insights" },
    ],
    keyPoints: [
      "Mood and energy tracking (1-10 scale)",
      "Environment and sound level context",
      "Optional notes for reflection",
      "Monthly summaries with gentle insights",
      "Gentle reminders with quiet hours",
    ],
  },
  "visual-routines": {
    title: "Visual Routines",
    tagline: "Predictability you can see",
    icon: ListChecks,
    color: "brand-peach",
    bgColor: "bg-brand-peach/15",
    iconColor: "text-[#D4956A]",
    description:
      "Build visual, step-by-step routines for your day. Morning rituals, wind-down sequences, transition guides — with optional reminders that nudge, never nag.",
    extendedDescription:
      "Visual Routines turn the abstract idea of 'getting through the day' into clear, manageable steps. Create routines with icons, set time estimates for each step, and choose daily, weekly, or monthly schedules. When it's time, the execution view guides you through one step at a time — large, clear, and calm. Optional gentle reminders help you stay on track without creating pressure.",
    screenshots: [
      { url: `${S}/routines-1.webp`, caption: "Create Routine — Build your visual routine" },
      { url: `${S}/routines-2.webp`, caption: "Step-by-Step — Execute your routine one step at a time" },
      { url: `${S}/routines-3.webp`, caption: "Transitions — Step-by-step guides for tricky moments" },
    ],
    keyPoints: [
      "Multi-step visual routines with icons",
      "Transition guides for tricky moments (leaving the house, bedtime, school)",
      "Daily, weekly, or monthly scheduling",
      "Step-by-step execution view",
      "Optional gentle reminders",
    ],
  },
  "communication-cards": {
    title: "Communication Cards",
    tagline: "Say what you need, even when words are hard",
    icon: MessageSquareText,
    color: "brand-blue",
    bgColor: "bg-brand-blue/10",
    iconColor: "text-brand-blue",
    description:
      "Quick-tap cards with text-to-speech for moments when verbal communication feels difficult. Pre-built phrases for needs, feelings, and requests — plus the ability to create your own.",
    extendedDescription:
      "Communication Cards provide a way to express yourself when speaking feels difficult. Tap a card to have it spoken aloud with text-to-speech. Cards are organised into categories — Needs, Feelings, and Requests — and you can create your own custom cards with large, clear icons. It's communication on your terms, at your pace.",
    screenshots: [
      { url: `${S}/comms-1.webp`, caption: "My Cards — Tap a card to speak" },
    ],
    keyPoints: [
      "Tap to speak with text-to-speech",
      "Categories: Needs, Feelings, Requests",
      "Create custom cards",
      "Large, clear icons",
      "Works offline",
    ],
  },
  "community-places": {
    title: "Community Places",
    tagline: "Find spaces that feel right",
    icon: MapPin,
    color: "brand-sage",
    bgColor: "bg-brand-sage/20",
    iconColor: "text-[#7EAD87]",
    description:
      "Discover sensory-friendly locations shared by the NeuroNest community. Libraries, cafes, parks — filtered by your sensory profile, with quiet-time predictions and community ratings. Currently UK-based, with global expansion on the way.",
    extendedDescription:
      "Community Places is a growing directory of sensory-friendly locations shared by real people. Browse by category (nature, sport, retail, and more), switch between list and map views, and filter by sensory features like quiet times, dimmed lights, or ear defenders available. You can also add your own places to help others in the community find spaces that feel right. Currently available across the UK, with plans to expand worldwide — this feature is in active development and constantly being updated with new places and improvements.",
    screenshots: [
      { url: `${S}/places-1.webp`, caption: "Community Places — Browse sensory-friendly locations" },
      { url: `${S}/places-2.webp`, caption: "Map View — Sensory pins with colour-coded categories" },
      { url: `${S}/places-3.webp`, caption: "Add a Place — Share sensory-friendly locations" },
    ],
    keyPoints: [
      "Sensory-friendly place directory",
      "'Best Match' based on your sensory profile",
      "Map view with sensory pins",
      "Community ratings and quiet-time predictions",
      "Add your own places to help others",
      "Currently UK-based — global expansion coming soon",
    ],
  },
  insights: {
    title: "Gentle Insights",
    tagline: "Patterns, not performance metrics",
    icon: BarChart3,
    color: "brand-purple",
    bgColor: "bg-brand-purple/10",
    iconColor: "text-brand-purple",
    description:
      "See your emotional patterns and trends over time — presented as gentle reflections, not scores to beat. AI-powered weekly summaries help you understand your experience without judgment.",
    extendedDescription:
      "Insights gathers your check-in data and presents it as gentle patterns. See your emotional tone, overload risk levels, masking fatigue indicators, and sensory confidence — all explained in plain, supportive language. Personalised recommendations help you plan your day around what works for you. No competitive metrics, no guilt — just understanding.",
    screenshots: [
      { url: `${S}/insights-1.webp`, caption: "Your Insights — Emotional tone and overload risk" },
      { url: `${S}/insights-2.webp`, caption: "Recommendations — Personalised suggestions" },
    ],
    keyPoints: [
      "Weekly emotional summaries",
      "Overload risk awareness",
      "Masking fatigue tracking",
      "Personalised recommendations",
      "No competitive metrics",
    ],
  },
  "my-space": {
    title: "My Space",
    tagline: "Your personalised comfort hub",
    icon: LayoutDashboard,
    color: "brand-teal",
    bgColor: "bg-brand-teal/10",
    iconColor: "text-brand-teal",
    description:
      "A personalised dashboard bringing together your comfort anchors, favourite fidget tools, sensory circuits, and sensory mood tracking — all in one calm place.",
    extendedDescription:
      "My Space is your home within NeuroNest. It brings together the things that help you most — your comfort anchors (photos, sounds, or memories that ground you), quick access to your favourite fidget tools and circuits, and a visual snapshot of your sensory mood over time. Think of it as a personalised calm room that's always in your pocket.",
    screenshots: [
      { url: `${S}/myspace-1.webp`, caption: "My Space — Comfort anchors, fidgets, and sensory mood" },
    ],
    keyPoints: [
      "Comfort anchors — photos, sounds, and memories that ground you",
      "Quick access to your favourite fidget tools",
      "Sensory circuits at your fingertips",
      "Visual sensory mood tracking",
      "Fully customisable to your needs",
    ],
  },
  "sensory-profile": {
    title: "Sensory Profile",
    tagline: "Understanding how you experience the world",
    icon: SlidersHorizontal,
    color: "brand-purple",
    bgColor: "bg-brand-purple/10",
    iconColor: "text-brand-purple",
    description:
      "Set up your sensory sensitivities across noise, light, touch, movement, and more. Your profile helps NeuroNest personalise everything — from place recommendations to comfort suggestions.",
    extendedDescription:
      "Your Sensory Profile is the foundation of your NeuroNest experience. Rate your sensitivity levels across noise, light, touch, movement, and other senses. NeuroNest uses this to match you with sensory-friendly places, tailor recommendations, and help the people around you understand your needs. Update it anytime — your sensory experience isn't fixed, and neither is your profile.",
    screenshots: [
      { url: `${S}/sensory-1.webp`, caption: "Sensory Profile — Noise, light, touch, and movement sensitivities" },
    ],
    keyPoints: [
      "Rate sensitivity: noise, light, touch, movement, and more",
      "Powers 'Best Match' in Community Places",
      "Helps personalise recommendations and insights",
      "Shareable with carers and support networks",
      "Update anytime — your experience isn't fixed",
    ],
  },
  "individual-mode": {
    title: "For Myself",
    tagline: "All the core tools, designed around you",
    icon: LayoutDashboard,
    color: "brand-purple",
    bgColor: "bg-brand-purple/10",
    iconColor: "text-brand-purple",
    video: "https://www.youtube-nocookie.com/embed/4PVf37KV0bA",
    description:
      "Full access to every NeuroNest feature — check-ins, routines, fidget tools, communication cards, community places, insights, and your personalised sensory profile. Your space, your pace.",
    extendedDescription:
      "Individual mode is the complete NeuroNest experience. Set up your sensory profile during onboarding, and the app adapts to you. Track how steady things feel with quick check-ins, build visual routines for your day, find sensory-friendly places nearby, and access calming tools whenever you need them. Your dashboard shows quick actions for everything — Check In, View Check-Ins, Create Routine, Community Places, Fidget Games, Circuits, and more. No pressure. This is just for your awareness.",
    screenshots: [
      { url: `${S}/individual-1.webp`, caption: "You're all set — Your NeuroNest is ready" },
      { url: `${S}/individual-2.webp`, caption: "Home Dashboard — Quick actions for everything you need" },
      { url: `${S}/individual-3.webp`, caption: "Quick Check-In — How steady do things feel right now?" },
    ],
    keyPoints: [
      "Full access to all 9 core features",
      "Personalised sensory profile",
      "Quick check-ins — no pressure, just awareness",
      "Home dashboard with all quick actions",
      "Insights and recommendations just for you",
      "Your pace, your space, your way",
    ],
  },
  "parent-mode": {
    title: "Parent Mode",
    tagline: "Stay connected, stay informed",
    icon: Users,
    color: "brand-teal",
    bgColor: "bg-brand-teal/10",
    iconColor: "text-brand-teal",
    video: "https://www.youtube-nocookie.com/embed/JTd1Q7A2qZo",
    description:
      "Create routines, monitor check-ins, and receive real-time push notifications — all from your own dashboard, designed to keep you connected to your child's experience.",
    extendedDescription:
      "Parent Mode gives you a dedicated dashboard to manage your child's NeuroNest experience. Create and assign routines, view check-in patterns and emotional trends, see activity summaries, and receive push notifications when your child uses calming tools or checks in. It's designed to support you in supporting them — without surveillance, without pressure.",
    screenshots: [
      { url: `${S}/parent-1.webp`, caption: "Parent Dashboard — Child profiles and quick actions" },
      { url: `${S}/parent-2.webp`, caption: "Child Overview — Activity summary and sensory profile" },
      { url: `${S}/parent-3.webp`, caption: "Real-Time Notifications — Stay connected" },
    ],
    keyPoints: [
      "Dedicated parent dashboard with child profiles",
      "Create and assign routines for your child",
      "Monitor check-ins and emotional trends",
      "Real-time push notifications for tool usage",
      "Activity summaries and sensory profile overview",
    ],
  },
  "carer-mode": {
    title: "Carer Mode",
    tagline: "Professional support, personal care",
    icon: HeartHandshake,
    color: "brand-sage",
    bgColor: "bg-brand-sage/20",
    iconColor: "text-[#7EAD87]",
    description:
      "Designed for professional carers and support workers. Access the tools you need to support neurodivergent individuals in your care — with the same calm, structured interface.",
    extendedDescription:
      "Carer Mode provides a tailored experience for professional carers, support workers, and anyone in a caring role outside the family. Choose 'As a carer' during onboarding to access the tools and views designed for your role — including routine management, check-in monitoring, and communication support. Built with the same sensory-safe principles, because carers deserve calm tools too.",
    screenshots: [
      { url: `${S}/carer-1.webp`, caption: "Choose Your Role — Select 'As a carer' during onboarding" },
      { url: `${S}/carer-2.webp`, caption: "Carer Insights — Select a person to view their insights" },
      { url: `${S}/carer-3.webp`, caption: "Paul's Insights — Emotional tone, overload risk, and more" },
      { url: `${S}/carer-4.webp`, caption: "Individual Profile — Quick actions, sensory profile, and observations" },
    ],
    keyPoints: [
      "Dedicated carer role during onboarding",
      "View insights for each person in your care",
      "Monitor emotional tone, overload risk, and masking fatigue",
      "Quick actions: Check-In, Routines, Circuits, Cards",
      "Sensory profile overview with challenges and comforts",
      "Same sensory-safe design principles",
    ],
  },
  "child-mode": {
    title: "Child Mode",
    tagline: "A calm, safe space just for them",
    icon: Smile,
    color: "brand-peach",
    bgColor: "bg-brand-peach/15",
    iconColor: "text-[#D4956A]",
    description:
      "A simplified, age-appropriate interface with PIN-based login. Children get quick access to fidget tools, circuits, transitions, breathing, grounding, and communication cards.",
    extendedDescription:
      "Child Mode gives young users their own calm, simplified space within NeuroNest. They log in with a simple 4-digit PIN — no email needed. The interface is stripped back to what matters: quick access to fidget tools, sensory circuits, transitions, breathing exercises, grounding techniques, and their communication cards. Children can link to their parent's device with a simple 6-character code, and their settings are protected behind the parent's password.",
    screenshots: [
      { url: `${S}/child-1.webp`, caption: "Child Dashboard — Quick access to all tools" },
      { url: `${S}/child-2.webp`, caption: "Link to Parent — Simple 6-character code" },
    ],
    keyPoints: [
      "Simplified, age-appropriate interface",
      "PIN-based login — no email needed",
      "Quick access to fidget tools, circuits, and breathing",
      "Link to parent's device with a 6-character code",
      "Settings protected behind parent's password",
    ],
  },
  "support-resources": {
    title: "Support & Resources",
    tagline: "You don't have to do this alone",
    icon: BookHeart,
    color: "brand-rose",
    bgColor: "bg-brand-rose/15",
    iconColor: "text-brand-rose",
    description:
      "Parent support services, SEND advocacy, guides on sensory profiles, transitions, and emotional regulation — all in one calm, accessible place.",
    extendedDescription:
      "The Support & Resources section is designed especially for parents and carers navigating the SEND landscape. Find local services like SEND Advocacy, Council SEND Teams, Parent Support Circles, SEND Family Hubs, Short Breaks Services, and EHCP Workshops. Plus, access short, calm guides on understanding sensory profiles, preparing for new environments, what to expect at SEN sessions, emotional regulation strategies, supporting transitions, and advocacy without overwhelm. Everything you need, without the information overload.",
    screenshots: [
      { url: `${S}/support-1.webp`, caption: "Support for Parents — Local services and support networks" },
      { url: `${S}/support-2.webp`, caption: "Guides & Helpful Information — Short, calm guides" },
    ],
    keyPoints: [
      "SEND Advocacy Service and Council SEND Team links",
      "Parent Support Circle and SEND Family Hub",
      "Short Breaks Service and EHCP Workshop info",
      "Guides: Sensory Profiles, New Environments, SEN Sessions",
      "Emotional regulation and transition support strategies",
      "Advocacy guidance without the overwhelm",
    ],
  },
};

// Shared features everyone gets (3x3 grid)
export const FEATURES_LIST = [
  "calm-toolkit",
  "check-ins",
  "visual-routines",
  "communication-cards",
  "community-places",
  "insights",
  "my-space",
  "sensory-profile",
  "support-resources",
];

// Mode cards (child next to parent)
export const MODES_LIST = [
  "individual-mode",
  "parent-mode",
  "child-mode",
  "carer-mode",
];
